<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <ShieldCheck class="w-5 h-5 text-brand-600" />
            {{ form.id ? 'Editar Tipo de Garantía' : 'Nuevo Tipo de Garantía' }}
          </h2>
          <p class="text-xs text-slate-500">Clasificación de resguardos para formalización.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="p-6 overflow-y-auto">
        <form id="collateralForm" @submit.prevent="handleSubmit" novalidate class="space-y-6">

          <div>
            <label :class="labelClasses">Nombre de la Garantía</label>
            <input v-model="form.name" type="text" placeholder="Ej: Hipotecaria (Inmueble)" required :class="inputClasses" />
          </div>

          <div>
            <label :class="labelClasses">Descripción de requisitos</label>
            <textarea v-model="form.description" rows="3" placeholder="Detalle qué documentos o bienes se requieren..." :class="inputClasses"></textarea>
          </div>

          <div class="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl mt-4">
            <div>
              <h4 class="text-sm font-bold text-slate-900 dark:text-slate-100">Estado del Tipo</h4>
              <p class="text-xs text-slate-500">Permitir que sea seleccionada en solicitudes.</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" v-model="form.isActive" class="sr-only peer">
              <div :class="switchClasses"></div>
            </label>
          </div>

        </form>
      </div>

      <div class="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
        <button @click="$emit('close')" type="button" :class="btnSecondary">Cancelar</button>
        <button type="submit" form="collateralForm" :disabled="isSaving" :class="btnPrimary">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          <Save v-else class="w-4 h-4" />
          {{ form.id ? 'Guardar Cambios' : 'Crear Tipo' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import { X, Save, Loader2, ShieldCheck } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const props = defineProps({ show: Boolean, collateralToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const isSaving = ref(false);

// ESTILOS EN CONSTANTES
const inputClasses = "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all text-slate-700 dark:text-slate-200";
const labelClasses = "text-[10px] font-bold text-slate-500 uppercase mb-1 block";
const switchClasses = "w-11 h-6 bg-slate-300 rounded-full peer dark:bg-slate-700 peer-checked:bg-emerald-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white";
const btnPrimary = "px-6 py-2.5 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto";
const btnSecondary = "px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors w-full sm:w-auto";

const form = reactive({
  id: null,
  name: '',
  description: '',
  isActive: true
});

watch(() => props.show, (val) => {
  if (val) {
    if (props.collateralToEdit && Object.keys(props.collateralToEdit).length > 0) {
      // USAMOS DESESTRUCTURACIÓN PARA CLONAR EL OBJETO Y ROMPER LA REFERENCIA
      const data = { ...props.collateralToEdit };

      form.id = data.id;
      form.name = data.name || '';
      form.description = data.description || '';
      form.isActive = data.status !== 'INACTIVE';
    } else {
      // Limpieza total para nuevos registros
      Object.assign(form, {
        id: null, name: '', description: '', isActive: true
      });
    }
  }
});

const handleSubmit = async () => {
  if (!form.name) return showError('Debe proporcionar un nombre para el tipo de garantía.');

  isSaving.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    const payload = {
      company_id: user.company,
      name: form.name,
      description: form.description,
      status: form.isActive ? 'ACTIVE' : 'INACTIVE'
    };

    if (form.id) {
      await apiClient.put(`/collateral_types/${form.id}`, payload);
      showSuccess('Tipo de garantía actualizado.');
    } else {
      await apiClient.post('/collateral_types', payload);
      showSuccess('Tipo de garantía creado.');
    }

    emit('saved');
    emit('close');
  } catch (error) {
    showError(error.response?.data?.message || 'Error al guardar.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
</style>